import { createTheme } from "@mui/material/styles";
import type { PaletteOptions } from "@mui/material/styles";
import { DARK_MODE_THEME, LIGHT_MODE_THEME } from "../utils/constants";
import { axisClasses, legendClasses, chartsGridClasses } from "@mui/x-charts";
declare module "@mui/material/styles" {
  interface TypeAction {
    pulse?: string;
    hoverFooter?: string;
  }
  interface Components {
    MuiChartsAxis?: {
      styleOverrides?: {
        root?: any;
      };
    };
    MuiChartsTooltip?: {
      styleOverrides?: {
        mark?: any;
        table?: any;
      };
    };
    MuiChartsLegend?: {
      styleOverrides?: {
        root?: any;
      };
    };
    MuiChartsGrid?: {
      styleOverrides?: {
        root?: any;
      };
    };
  }
}

interface BackgroundCustom {
  green: string;
  darkGreen: string;
  light: string;
  blog: string;
  tiers: string;
  orange: string;
  wheat: string;
  table: string;
  purple: string;
  olive: string;
  oliveGreen: string;
  explorerGreen: string;
  searchInput: string;
  searchComponent: string;
  featureLandCard: string;
  infoCard: string;
  homeBanner: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    border: {
      default: string;
      dark: string;
      darker: string;
    };
    back: Partial<BackgroundCustom>;
  }

  interface PaletteOptions {
    border?: {
      default?: string;
      dark?: string;
      darker?: string;
    };
    back: Partial<BackgroundCustom>;
  }
}

type CustomPalettes = {
  light: PaletteOptions;
  dark: PaletteOptions;
};

const palettes: CustomPalettes = {
  light: {
    border: {
      default: "#ccc",
      dark: "#3d3d3d",
      darker: "#121212",
    },
    divider: "#3d3d3d",
    background: {
      default: "#fff",
      paper: "#f3f3f3",
    },
    text: {
      primary: "#000",
      secondary: "#fff",
      disabled: "#565656",
    },
    back: {
      green: "#969A82",
      darkGreen: "#2b3501",
      wheat: "#b09f41",
      light: "#d7eefd",
      tiers: "#dbdbdb",
      orange: "#c78154",
      blog: "#fcf9ee",
      table: "#ebe1dd",
      purple: "#b594f2",
      olive: "#a49f7f",
      oliveGreen: "#959a80",
      explorerGreen: "#2b3501",
      searchInput: "#fdf9ed",
      searchComponent: "#d1efff",
      featureLandCard: "#fdf9ed",
      infoCard: "#f2f2f2",
      homeBanner: "#333300",
    },
    success: {
      main: "#217c11",
      light: "#30af1a",
      dark: "#103d0a",
    },
    warning: {
      main: "#d37d4a",
    },
    primary: {
      main: "#000",
      light: "#54de59",
      dark: "#255d29",
    },
    info: {
      main: "#29aae0",
      light: "#42c8ff",
      dark: "#003ec4",
    },
    secondary: {
      main: "#c78154",
      light: "#c78154",
      dark: "#93613f",
    },
    action: {
      pulse: "#8CC63F",
      // active: "rgba(75,250,0,0.71)",
      active: "#000",
      hover: "#494949",
      hoverFooter: "#999999",
      disabled: "rgba(0, 0, 0, 0.4)",
      disabledBackground: "rgba(0, 0, 0, 0.26)",
    },
  },
  dark: {
    border: {
      default: "#ccc",
      dark: "#3d3d3d",
    },
    divider: "#3d3d3d",
    back: {
      green: "#969A82",
      darkGreen: "#2b3501",
      wheat: "#b09f41",
      light: "#d7eefd",
      tiers: "#dbdbdb",
      orange: "#c78154",
      blog: "#fcf9ee",
      table: "#121212",
      purple: "#b594f2",
      olive: "#a49f7f", // added for background color
      oliveGreen: "#959a80", // added for terminal data
      explorerGreen: "#2b3501", // added for call-to-action prompts
      searchInput: "#fdf9ed",
      searchComponent: "#d1efff",
      featureLandCard: "#fdf9ed",
      infoCard: "#f2f2f2",
      homeBanner: "#333300",
    },
    background: {
      default: "#121212",
      paper: "#1a1a1a",
    },
    primary: {
      main: "#fff", // rgb(78, 131, 69)
      light: "#50e75f",
      dark: "#1c8923",
    },
    secondary: {
      main: "#00bdff",
      light: "#42c8ff",
      dark: "#1e90ff",
    },
    success: {
      main: "#30af1a",
      light: "#30af1a",
      dark: "#217c11",
    },
    text: {
      primary: "#fff",
      secondary: "#000",
      disabled: "#ccc",
    },
    action: {
      active: "#2ad500",
      hover: "rgba(255,255,255,0.6)",
      hoverFooter: "#999999",
      disabled: "#3d3d3d",
      disabledBackground: "rgba(255, 255, 255, 0.3)",
    },
  },
};

// Width 1200px
// Big margins 48px
// Small margins 24px
// (14pt, 26pt, 50pt)
// Numbers, values, and data: IBM Plex Mono
// (14pt, 30pt, 50pt)

// Width 900px
// Big margins 24px
// Small margins 12px
// (10pt, 14pt, 26pt)
// Numbers, values, and data: IBM Plex Mono
// (10pt, 14pt, 26pt)

// Width 600px
// Big margins 12px
// Small margins 6px
// (8pt, 12pt, 18pt)
// Numbers, values, and data: IBM Plex Mono
// (8pt, 12pt, 18pt)

export const getAppTheme = (mode: typeof LIGHT_MODE_THEME | typeof DARK_MODE_THEME) => {
  return createTheme({
    // transitions: {
    //
    // },
    shape: {
      borderRadius: 16,
    },
    palette: {
      mode,
      ...palettes[mode],
    },
    breakpoints: {
      values: {
        xs: 300,
        sm: 560,
        md: 992,
        lg: 1200,
        xl: 1560,
      },
    },
    spacing: 2,
    typography: {
      body1: {
        fontSize: 16,
        lineHeight: 1.2,
      },
      h1: {
        fontSize: 26,
      },
      h2: {
        fontSize: 18,
      },
      fontSize: 16,
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    components: {
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          componentsProps: {
            arrow: {
              sx: {
                color: "text.secondary",
              },
            },
            tooltip: {
              sx: {
                border: "1px solid #dee2e6",
                boxShadow: "rgba(0, 0, 0, 0.15) 0px 4px 8px 0px",
                borderRadius: "8px",
                color: "text.primary",
                backgroundColor: "text.secondary",
              },
            },
          },
        },
      },
      MuiLink: {
        defaultProps: { color: "success.main" },
        variants: [
          {
            props: { color: "success.main" },
            style: {
              ":hover": {
                color: (palettes[mode]?.success as any).dark,
              },
            },
          },
          {
            props: { color: "text.primary" },
            style: {
              textDecoration: "none",
              ":hover": {
                textDecoration: "underline",
                color: palettes[mode]?.text?.primary,
              },
            },
          },
        ],
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: "small",
        },
        styleOverrides: {
          fontSizeLarge: {
            fontSize: 44,
            "@media (max-width: 992px)": {
              fontSize: 34,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            border: "var(--border)",
            borderColor: palettes[mode]?.divider,
            background: "none",
            padding: 24,
            maxWidth: "1560px",
            margin: "0 auto",
            width: "100%",
            justifyContent: "space-between",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: "none",
            boxShadow: "none",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          sizeMedium: {
            width: 44,
            height: 44,
            "@media (max-width: 992px)": {
              width: 34,
              height: 34,
            },
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "contained", color: "primary" },
            style: {
              ":disabled": {
                color: palettes[mode]?.text?.disabled,
                borderColor: "transparent",
                backgroundColor: palettes[mode]?.action?.disabled,
              },
              boxShadow: "none",
              fontWeight: "normal",
              color: palettes[mode]?.text?.secondary,
              backgroundColor: palettes[mode]?.text?.primary,
              border: "1px solid",
              borderColor: palettes[mode]?.text?.primary,
              ":hover": {
                boxShadow: "none",
                border: "1px solid",
                borderColor: palettes[mode]?.text?.primary,
                color: palettes[mode]?.text?.primary,
                background: palettes[mode]?.background?.paper,
              },
            },
          },
        ],
        defaultProps: {
          size: "large",
          variant: "contained",
          color: "primary",
          disableTouchRipple: false,
        },
        styleOverrides: {
          focusVisible: false,
          sizeMedium: {
            fontWeight: "normal",
            fontSize: 16,
          },
          sizeSmall: {
            fontWeight: "normal",
            fontSize: 12,
          },
          sizeLarge: {
            fontWeight: "normal",
            padding: "8px 24px",
            textTransform: "capitalize",
            transition: "0.5s width",
            fontSize: 16,
            width: "180px",
            minWidth: "max-content",
            height: "44px",
            "@media (max-width: 992px)": {
              fontSize: 14,
              width: "140px",
              height: "36px",
            },
          },
          text: {
            color: palettes[mode]?.text?.primary,
            fontWeight: "normal",
            textTransform: "none",
            ":hover": {
              opacity: "0.7",
              backgroundColor: "initial",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderWidth: 1,
            borderColor: palettes[mode]?.border?.darker,
          },
          root: {
            "&.Mui-disabled": {
              color: "#494949",
              borderColor: "none",
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
        },
        defaultProps: {
          variant: "rounded",
          animation: "pulse",
          color: "white",
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          standard: {
            color: palettes[mode]?.text?.primary,
          },
          root: {
            "&.Mui-focused": {
              color: palettes[mode]?.text?.primary,
            },
            "&.MuiFormLabel-filled": {
              color: palettes[mode]?.text?.primary,
            },
          },
          formControl: {
            color: palettes[mode]?.text?.disabled,
          },
        },
        defaultProps: {
          classes: {
            focused: "Mui-focused",
            filled: "MuiFormLabel-filled",
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: "0",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: "none",
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          marginThreshold: 12,
        },
        styleOverrides: {
          paper: {
            // margin: 12,
            // maxWidth: "calc(100% - 24px)",
            width: "max-content",
            border: "1px solid #dee2e6",
            boxShadow: "rgba(0, 0, 0, 0.15) 0px 8px 16px 0px",
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            margin: 12,
            width: "100%",
            maxWidth: "400px",
            border: "1px solid #dee2e6",
            boxShadow: "rgba(0, 0, 0, 0.15) 0px 8px 16px 0px",
            borderRadius: 8,
          },
        },
        // defaultProps: {
        //   root: {
        //     paper: {
        //       border: "1px solid #dee2e6",
        //       boxShadow: "rgba(0, 0, 0, 0.15) 0px 8px 16px 0px",
        //       borderRadius: "8px",
        //       backgroundColor: "background.paper",
        //     },
        //   },
        // },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: "16px",
            textTransform: "uppercase",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            fontWeight: "normal",
            borderColor: palettes[mode]?.divider,
            "@media (max-width: 992px)": {
              textTransform: "capitalize",
              fontSize: "14px",
            },
          },
          body: {
            fontSize: "16px",
            boxSizing: "border-box",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            borderColor: palettes[mode]?.divider,
            "@media (max-width: 992px)": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiChartsAxis: {
        styleOverrides: {
          root: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
            [`& .${axisClasses.line}`]: {
              stroke: "hsl(220, 30%, 6%)",
            },
            [`& .${axisClasses.tick}`]: { stroke: "hsl(220, 20%, 25%)" },
            [`& .${axisClasses.tickLabel}`]: {
              fill: "black",
              fontWeight: 400,
            },
            ...theme.applyStyles("dark", {
              [`& .${axisClasses.line}`]: {
                stroke: "hsl(220, 20%, 25%)",
              },
              [`& .${axisClasses.tick}`]: { stroke: "hsl(220, 20%, 25%)" },
              [`& .${axisClasses.tickLabel}`]: {
                fill: "hsl(220, 20%, 80%)",
                fontWeight: 800,
              },
            }),
          }),
        },
      },
      MuiChartsTooltip: {
        styleOverrides: {
          mark: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
            ry: 6,
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
          }),
          table: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            background: "hsl(220, 35%, 3%)",

            ...theme.applyStyles("light", {
              background: "hsl(0, 0%, 100%)",
            }),
          }),
        },
      },
      MuiChartsLegend: {
        styleOverrides: {
          root: {
            [`& .${legendClasses.mark}`]: {
              ry: 6,
            },
          },
        },
      },
      MuiChartsGrid: {
        styleOverrides: {
          root: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
            [`& .${chartsGridClasses.line}`]: {
              stroke: "hsl(220, 20%, 25%)",
              strokeDasharray: "4 2",
              strokeWidth: 0.8,
            },
            ...theme.applyStyles("light", {
              [`& .${chartsGridClasses.line}`]: {
                stroke: "hsl(225, 24.00%, 9.80%)",
                strokeDasharray: "4 2",
                strokeWidth: 0.8,
              },
            }),
          }),
        },
      },
    },
  });
};

// Primary
// palette.primary.light #42a5f5
// palette.primary.main #1976d2
// palette.primary.dark #1565c0
//
// Secondary
//
// palette.secondary.light #ba68c8
// palette.secondary.main #9c27b0
// palette.secondary.dark #7b1fa2
//
// Error
//
// palette.error.light #ef5350
// palette.error.main #d32f2f
// palette.error.dark #c62828
//
// Warning
//
// palette.warning.light #ff9800
// palette.warning.main #ed6c02
// palette.warning.dark #e65100
//
// Info
//
// palette.info.light #03a9f4
// palette.info.main #0288d1
// palette.info.dark #01579b
//
// Success
//
// palette.success.light #4caf50
// palette.success.main #2e7d32
// palette.success.dark #1b5e20
